import { useState, useEffect } from "react";
import {
  ingestKnowledge,
  listEmployeePacks,
  listTenants,
  type EmployeeFacadeProfile,
  type EmployeePackList,
  type IngestKnowledgeResult,
} from "../lib/api";
import type { Tenant } from "../types";

type UploadResult = Partial<IngestKnowledgeResult> & { error?: string };

export default function KnowledgeBasePage() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [employeePacks, setEmployeePacks] = useState<EmployeePackList | null>(null);

  // Which tenant is being managed
  const [activeTenantId, setActiveTenantId] = useState<string | null>(null);
  const [content, setContent] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [facadeProfiles, setFacadeProfiles] = useState<EmployeeFacadeProfile[]>([]);
  const [facadeProfilesLoading, setFacadeProfilesLoading] = useState(false);
  const [facadeProfilesError, setFacadeProfilesError] = useState("");
  const [facadeReload, setFacadeReload] = useState(0);
  const [selectedFacadeId, setSelectedFacadeId] = useState("");

  const activeTenantPack = employeePacks?.tenants?.find((tenant) => tenant.tenantId === activeTenantId);
  const activeCustomerPack = employeePacks?.packs.find(
    (pack) => pack.role === "customer_service" && pack.packId === activeTenantPack?.packId,
  );
  const facadeUploadRequired = activeTenantPack?.facadeKbEnabled === true && activeCustomerPack !== undefined;
  const facadeOptions = facadeUploadRequired ? (activeCustomerPack?.facades ?? []) : [];
  const selectedFacadeValid = !facadeUploadRequired
    || facadeOptions.some((facade) => facade.id === selectedFacadeId);

  useEffect(() => {
    void loadTenants();
    void loadEmployeePacks();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setFacadeProfiles([]);
    setFacadeProfilesError("");
    if (!activeTenantId || !facadeUploadRequired) {
      setFacadeProfilesLoading(false);
      return;
    }
    setFacadeProfilesLoading(true);
    listEmployeePacks(activeTenantId)
      .then((data) => {
        if (cancelled) return;
        setFacadeProfiles((data.facadeProfiles ?? []).filter((profile) => profile.tenantId === activeTenantId));
      })
      .catch((err) => {
        if (cancelled) return;
        setFacadeProfilesError(err instanceof Error ? err.message : "品牌知識庫狀態載入失敗");
      })
      .finally(() => {
        if (!cancelled) setFacadeProfilesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTenantId, facadeUploadRequired, facadeReload]);

  async function loadTenants() {
    setLoading(true);
    setError("");
    try {
      const data = await listTenants();
      if (data.success) {
        setTenants(data.tenants || []);
      } else {
        setError(data.error || "載入失敗");
      }
    } catch {
      setError("網路錯誤");
    } finally {
      setLoading(false);
    }
  }

  async function loadEmployeePacks() {
    try {
      const data = await listEmployeePacks();
      if (Array.isArray(data.packs)) setEmployeePacks(data);
    } catch {
      return;
    }
  }

  function openEditor(tenant: Tenant) {
    setActiveTenantId(tenant.tenant_id);
    setContent("");
    setUploadResult(null);
    setSelectedFacadeId("");
    setFacadeProfiles([]);
    setFacadeProfilesError("");
    void loadEmployeePacks();
  }

  function closeEditor() {
    setActiveTenantId(null);
    setContent("");
    setUploadResult(null);
    setSelectedFacadeId("");
    setFacadeProfiles([]);
    setFacadeProfilesError("");
  }

  async function handleUpload(tenantId: string) {
    const trimmed = content.trim();
    if (!trimmed) return;
    if (!selectedFacadeValid) {
      setUploadResult({ error: "請先選擇品牌，未選品牌不可上傳。" });
      return;
    }
    setUploading(true);
    setUploadResult(null);
    try {
      const data = facadeUploadRequired
        ? await ingestKnowledge(tenantId, trimmed, selectedFacadeId)
        : await ingestKnowledge(tenantId, trimmed);
      setUploadResult(data);
      if (data.success) {
        // Refresh tenant list to update chunk count
        await loadTenants();
        if (facadeUploadRequired) setFacadeReload((value) => value + 1);
        // Keep editor open but clear content
        setContent("");
      }
    } catch (err) {
      setUploadResult({ error: err instanceof Error ? err.message : "請求失敗" });
    } finally {
      setUploading(false);
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-100">📝 知識庫管理</h1>
        <button
          onClick={loadTenants}
          className="px-4 py-2 text-sm bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors"
        >
          🔄 重新載入
        </button>
      </div>

      {loading ? (
        <div className="text-slate-400">載入中...</div>
      ) : error ? (
        <div className="text-rose-400">❌ {error}</div>
      ) : tenants.length === 0 ? (
        <div className="text-slate-500">暫無租戶</div>
      ) : (
        <div className="space-y-4">
          {/* Tenant overview table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-slate-400 uppercase bg-slate-900 border-b border-slate-700">
                <tr>
                  <th className="px-4 py-3">租戶 ID</th>
                  <th className="px-4 py-3">Bot 名稱</th>
                  <th className="px-4 py-3">方案</th>
                  <th className="px-4 py-3">知識庫碎片</th>
                  <th className="px-4 py-3">操作</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((t) => {
                  const isActive = activeTenantId === t.tenant_id;
                  return (
                    <tr
                      key={t.tenant_id}
                      className={`border-b border-slate-800 transition-colors ${
                        isActive
                          ? "bg-emerald-900/15"
                          : "bg-slate-900/50 hover:bg-slate-800/50"
                      }`}
                    >
                      <td className="px-4 py-3 font-mono text-emerald-400 text-xs">
                        {t.tenant_id}
                      </td>
                      <td className="px-4 py-3">{t.bot_name || "—"}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium ${
                            t.plan_type === "paid"
                              ? "bg-emerald-500/20 text-emerald-400"
                              : t.plan_type === "trial"
                              ? "bg-amber-500/20 text-amber-400"
                              : "bg-rose-500/20 text-rose-400"
                          }`}
                        >
                          {t.plan_type}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-slate-300">
                          {t.kb_chunk_count ?? 0}
                        </span>
                        <span className="text-xs text-slate-500 ml-1">chunks</span>
                      </td>
                      <td className="px-4 py-3">
                        {isActive ? (
                          <button
                            onClick={closeEditor}
                            className="px-3 py-1 text-xs bg-slate-700 hover:bg-slate-600 text-slate-300 rounded transition-colors"
                          >
                            ✕ 收起
                          </button>
                        ) : (
                          <button
                            onClick={() => openEditor(t)}
                            className="px-3 py-1 text-xs bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 rounded transition-colors"
                          >
                            📝 管理上傳
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Inline editor for active tenant */}
          {activeTenantId && (
            <div className="bg-slate-900 rounded-xl border border-emerald-800/40 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-emerald-400">
                  📝 上傳知識庫 — {activeTenantId}
                </h2>
                <button
                  onClick={closeEditor}
                  className="px-3 py-1 text-xs bg-slate-800 hover:bg-slate-700 text-slate-400 rounded transition-colors"
                >
                  ✕ 關閉
                </button>
              </div>

              <div className="space-y-4">
                {facadeUploadRequired && (
                  <div>
                    <label htmlFor="kb-facade" className="block text-sm font-medium text-slate-300 mb-1">
                      品牌（必選）
                    </label>
                    <select
                      id="kb-facade"
                      value={selectedFacadeId}
                      onChange={(event) => setSelectedFacadeId(event.target.value)}
                      disabled={uploading}
                      className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-slate-100 focus:outline-none focus:border-emerald-500 disabled:opacity-50"
                    >
                      <option value="">請選擇品牌</option>
                      {facadeOptions.map((facade) => {
                        const profile = facadeProfiles.find(
                          (item) => item.tenantId === activeTenantId && item.facadeId === facade.id,
                        );
                        const name = facade.description ? `${facade.description}（${facade.id}）` : facade.id;
                        return (
                          <option key={facade.id} value={facade.id}>
                            {name}{profile ? ` · ${profile.chunkCount} chunks` : ""}
                          </option>
                        );
                      })}
                    </select>
                    {facadeProfilesLoading && <p className="text-xs text-slate-500 mt-1">正在載入各品牌 chunks…</p>}
                    {facadeProfilesError && (
                      <p role="alert" className="text-xs text-amber-300 mt-1">
                        品牌 chunks 載入失敗，仍可選擇品牌：{facadeProfilesError}
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    知識內容
                  </label>
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    rows={10}
                    placeholder="在此貼上該租戶的知識庫內容...&#10;例如：&#10;我們的退貨政策：&#10;&#10;購買後 14 天內可憑收據退貨。&#10;&#10;特價商品恕不退換。"
                    className="w-full px-4 py-3 bg-slate-950 border border-slate-700 rounded-lg text-slate-100 placeholder-slate-600 focus:outline-none focus:border-emerald-500 font-mono text-sm"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    系統會自動按段落切片並生成向量。
                    <span className="text-amber-400">
                      {facadeUploadRequired
                        ? " 只會完全覆蓋所選品牌的舊知識，不會覆蓋整個租戶或其他品牌。"
                        : " 舊內容會被完全覆蓋。"}
                    </span>
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                  <button
                    onClick={() => handleUpload(activeTenantId)}
                    disabled={uploading || !content.trim() || !selectedFacadeValid}
                    className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 text-white font-medium rounded-lg transition-colors"
                  >
                    {uploading ? "處理中..." : "🚀 上傳並覆蓋"}
                  </button>
                  {facadeUploadRequired && !selectedFacadeValid && (
                    <span className="text-xs text-amber-300">請先選擇有效品牌，未選品牌不可上傳。</span>
                  )}
                </div>

                {uploadResult && (
                  <div
                    className={`p-4 rounded-lg ${
                      uploadResult.success
                        ? "bg-emerald-500/10 border border-emerald-500/30"
                        : "bg-rose-500/10 border border-rose-500/30"
                    }`}
                  >
                    <p
                      className={`text-sm font-medium break-words ${
                        uploadResult.success
                          ? "text-emerald-400"
                          : "text-rose-400"
                      }`}
                    >
                      {uploadResult.success
                        ? `✅ 成功寫入 ${uploadResult.ingested} 筆知識碎片${uploadResult.facadeId ? `（品牌：${uploadResult.facadeId}）` : ""}`
                        : `❌ 錯誤：${uploadResult.error || "未知錯誤"}`}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
